import { CheckCircle, AlertTriangle, Download, Mail } from "lucide-react";
import { Button } from "../../components/ui/button";
import EfficiencyForecast from "../EfficiencyForecast";
import ROIAnalysisSection from "../ROIAnalysisSection";
import ScoreRing from "./ScoreRing";
import { getScoreLabel } from "./assessmentData";

export default function AssessmentResults({
  error, overallScore, roi, contactInfo, handleDownloadReport, handleEmailReport,
  emailingReport, reportEmailStatus, subScoreItems, topGaps, topOpportunities,
  allCategories, humanRiskScore,
}) {
  return (
    <div data-testid="assessment-results">
      <div className="text-center mb-10">
        <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#0077B3] mb-3">Your Results</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white mb-2" style={{ fontFamily: "Outfit" }}>
          Business Intelligence Score
        </h2>
        <p className="text-[#c0cfe0] text-sm">{contactInfo.company || "Your organization"}</p>
      </div>

      {error && (
        <div data-testid="assessment-submit-error" className="max-w-xl mx-auto mb-8 text-center p-4 border border-[#ef4444]/30 bg-[#ef4444]/5 rounded-md">
          <p className="text-[#ef4444] text-sm font-medium">
            We couldn’t save your contact info automatically. Your results below are still valid - please call us at (952) 941-7333 so we can make sure we follow up with you.
          </p>
        </div>
      )}

      {/* Primary score + Personalized Efficiency Forecast */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center mb-6">
        <div className="text-center">
          <ScoreRing score={overallScore} size={160} label="Overall Score" />
        </div>
        <EfficiencyForecast
          annualHoursReclaimed={roi.annualHoursReclaimed}
          monthlySavingsForecast={roi.monthlySavingsForecast}
          teamSize={roi.teamSize}
          weeklyHoursPerPerson={roi.weeklyHoursPerPerson}
        />
      </div>

      <div className="text-center mb-10">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button data-testid="download-report-btn" onClick={handleDownloadReport} className="bg-white/5 border border-[#0077B3]/30 hover:bg-[#0077B3]/10 text-white rounded-md font-semibold text-sm px-6 h-11">
            <Download className="w-4 h-4 mr-2" /> Download Executive ROI & Readiness Report
          </Button>
          <Button data-testid="email-report-btn" onClick={handleEmailReport} disabled={emailingReport} variant="ghost" className="text-[#94a8be] hover:text-white text-sm h-11 px-4">
            <Mail className="w-4 h-4 mr-2" /> {emailingReport ? "Sending..." : "Email Me This Report"}
          </Button>
        </div>
        {reportEmailStatus === "sent" && (
          <p data-testid="email-report-success" className="text-[#10b981] text-sm mt-3">Sent! Check your inbox at {contactInfo.email}.</p>
        )}
        {reportEmailStatus === "error" && (
          <p data-testid="email-report-error" className="text-[#ef4444] text-sm mt-3">Couldn’t send that email - please use the Download button instead.</p>
        )}
      </div>

      {/* 6 score grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-12">
        {subScoreItems.map((item) => {
          const s = getScoreLabel(item.score);
          return (
            <div key={item.label} className="bg-white/[0.03] border border-white/8 rounded-md p-4 text-center">
              <item.icon className="w-4 h-4 mx-auto mb-2" style={{ color: s.color }} />
              <p className="stat-number text-2xl text-white">{item.score}</p>
              <p className="text-[#c0cfe0] text-xs mt-1">{item.label}</p>
              <p className="text-[10px] mt-0.5" style={{ color: s.color }}>{s.label}</p>
            </div>
          );
        })}
      </div>

      {/* Top 3 Gaps + Top 3 Opportunities */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        <div>
          <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <AlertTriangle className="w-5 h-5 text-[#ef4444]" /> Top Gaps
          </h3>
          <div className="space-y-3">
            {topGaps.map((gap) => (
              <div key={gap.id} className="p-4 rounded-md bg-[#ef4444]/5 border border-[#ef4444]/10">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-white text-sm font-medium">{gap.label}</p>
                  <span className="text-xs font-semibold" style={{ color: getScoreLabel(gap.score).color }}>{gap.score}/100</span>
                </div>
                <p className="text-[#c0cfe0] text-xs leading-relaxed">{gap.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <CheckCircle className="w-5 h-5 text-[#10b981]" /> Top Opportunities
          </h3>
          <div className="space-y-3">
            {topOpportunities.map((opp) => (
              <div key={opp.id} className="p-4 rounded-md bg-[#10b981]/5 border border-[#10b981]/10">
                <p className="text-white text-sm font-medium mb-1">{opp.label}</p>
                <p className="text-[#c0cfe0] text-xs leading-relaxed">{opp.opportunity}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Category breakdown bars */}
      <div className="mb-12">
        <h3 className="text-white font-bold text-sm mb-4 uppercase tracking-wider">Full Breakdown</h3>
        <div className="space-y-3">
          {allCategories.map((cat) => {
            const s = getScoreLabel(cat.score);
            return (
              <div key={cat.id} className="flex items-center gap-4">
                <span className="text-[#c0cfe0] text-xs w-44 flex-shrink-0">{cat.label.replace(" Gap", "").replace(" Dependence", "").replace("Uncontrolled ", "").replace("Untested ", "")}</span>
                <div className="flex-1 bg-white/5 h-2 rounded-full">
                  <div className="h-2 rounded-full transition-all duration-700" style={{ width: `${cat.score}%`, backgroundColor: s.color }} />
                </div>
                <span className="text-xs font-semibold w-12 text-right" style={{ color: s.color }}>{cat.score}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {humanRiskScore === null && (
        <div className="text-center mb-8 p-4 border border-[#0077B3]/20 bg-[#0077B3]/5 rounded-md">
          <p className="text-[#0077B3] text-sm font-medium mb-1">Want a more accurate score?</p>
          <p className="text-[#c0cfe0] text-xs">Complete the Human Risk Simulation above to add real behavioral data to your Business Intelligence Score.</p>
        </div>
      )}

      <ROIAnalysisSection annualHoursReclaimed={roi.annualHoursReclaimed} monthlySavingsForecast={roi.monthlySavingsForecast} />

      {/* CTA */}
      <div className="bg-white rounded-lg p-8 text-center">
        <img src="/images/logo-circle.webp" alt="Veracity" loading="lazy" className="w-16 h-16 object-contain mx-auto mb-4 drop-shadow-[0_0_10px_rgba(0,119,179,0.4)]" />
        <h3 className="text-[#003B71] font-bold text-xl mb-2" style={{ fontFamily: "Outfit" }}>
          Review Your Results With Veracity
        </h3>
        <p className="text-[#4a5e78] text-sm mb-6 max-w-md mx-auto">
          We’ll walk through every score, prioritize the highest-impact improvements, and build a clear roadmap to reduce manual work, strengthen AI readiness, and lower operational risk.
        </p>
        <a href="tel:9529417333">
          <Button className="bg-[#003B71] hover:bg-[#002a52] text-white rounded-md font-semibold px-8 h-12">
            Schedule Your Review Call
          </Button>
        </a>
        <p className="text-[#4a5e78] text-xs mt-3">Or call (952) 941-7333</p>
      </div>
    </div>
  );
}
